import { createFileRoute, useParams } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useState } from "react";

import { ConfirmActionDialog } from "@/components/Dialog/ConfirmActionDialog";
import { VStack } from "@/components/Layout";
import { useCommonTranslation } from "@/hooks/useCommonTranslation";
import { defaultNS, useTranslation } from "@/lib/i18n";

import EditPlanDialog from "./-components/EditPlanDialog";
import { PlanHeader } from "./-components/Header";
import PlanHeaderSkeleton from "./-components/HeaderSkeleton";
import RestructuringChart from "./-components/RestructuringChart";
import RestructuringChartSkeleton from "./-components/RestructuringChartSkeleton";
import {
  useCompleteDepartmentRestructuring,
  useDepartmentRestructuringDetailQuery,
} from "./-query";
import { PlanDetailSearchSchema } from "./-zod-schema";

export const Route = createFileRoute("/_staff/organization/plan/$id/")({
  component: RouteComponent,
  validateSearch: zodValidator(PlanDetailSearchSchema),
  loaderDeps: ({ search }) => ({ search }),
});

function RouteComponent() {
  const { id } = useParams({ from: "/_staff/organization/plan/$id/" });
  const { data, isLoading } = useDepartmentRestructuringDetailQuery(id);
  const completeMutation = useCompleteDepartmentRestructuring(id);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isApproveConfirmOpen, setIsApproveConfirmOpen] = useState(false);
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.organization.plan.detailPage",
  });
  const tc = useCommonTranslation();

  const handleEdit = () => {
    setIsEditOpen(true);
  };

  const handleApprove = () => {
    setIsApproveConfirmOpen(true);
  };

  const handleConfirmApprove = () => {
    if (!data) return;
    setIsApproveConfirmOpen(false);
    completeMutation.mutate({ version: data.version });
  };

  if (isLoading || !data) {
    return (
      <VStack className="gap-4">
        <PlanHeaderSkeleton />
        <div className="px-12 py-5">
          <RestructuringChartSkeleton />
        </div>
      </VStack>
    );
  }

  return (
    <VStack className="gap-4">
      <PlanHeader
        data={data}
        onEdit={handleEdit}
        onApprove={handleApprove}
        isApproving={completeMutation.isPending}
      />

      <RestructuringChart detail={data} />

      <EditPlanDialog
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        data={data}
      />
      <ConfirmActionDialog
        isOpen={isApproveConfirmOpen}
        onClose={() => setIsApproveConfirmOpen(false)}
        onConfirm={handleConfirmApprove}
        title={{ resource: data.name, action: tc("action.approve") }}
        description={t("completeDialog.description")}
        okLabel={tc("action.approve")}
      />
    </VStack>
  );
}
